import React, { useState } from 'react'
import { useMobile } from '../../hooks/useMobile'
import { IconButton, Tooltip } from '@mui/material'
import PersonIcon from '@mui/icons-material/Person'
import MessageIcon from '@mui/icons-material/Message'
import FolderIcon from '@mui/icons-material/Folder'
import AssignmentIcon from '@mui/icons-material/Assignment'
import LocalFloristIcon from '@mui/icons-material/LocalFlorist'
import ThreeDRotation from '@mui/icons-material/ThreeDRotation'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { State } from '../../types/state'

interface TabItem {
  label: string
  value: string
}

interface AppNavBarProps {
  centerText?: string
  rightIcons: JSX.Element[]
  tabs?: TabItem[]
  activeTab?: string
  onTabChange?: (value: string) => void
}

const AppNavBar: React.FC<AppNavBarProps> = ({ centerText, rightIcons, tabs, activeTab, onTabChange }) => {
  const { isSuperadmin } = useSelector((state: State) => state.user)
  const isMobile = useMobile()
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const navigate = useNavigate()

  // Funkcja do zamykania dropdowna po kliknięciu poza
  React.useEffect(() => {
    if (!dropdownOpen) return
    const handleClick = (e: MouseEvent) => {
      const dropdown = document.getElementById('mobile-icons-dropdown')
      if ((dropdown != null) && !dropdown.contains(e.target as Node)) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [dropdownOpen])

  const defaultIcons = [
    <Tooltip title='Katalog produktów i modeli 3D' key='products'>
      <IconButton
        style={{ color: '#47a145' }}
        onClick={() => navigate('/products')}
        sx={{}}
      >
        <img src='/assets/images/icons/3d.png' alt='3D' style={{ width: 24, height: 24 }} />
      </IconButton>
    </Tooltip>,
    isSuperadmin
      ? (
        <Tooltip title='Katalog roślin' key='plants'>
          <IconButton
            style={{ color: '#47a145' }}
            onClick={() => navigate('/plants')}
            sx={{}}
          >
            <img src='/assets/images/icons/plant.png' alt='Rośliny' style={{ width: 24, height: 24 }} />
          </IconButton>
        </Tooltip>
        )
      : (
        <Tooltip title='Katalog roślin (dostępny wkrótce)' key='plants'>
          <span>
            <IconButton
              style={{ color: '#b0b8c1' }}
              disabled
              sx={{}}
            >
              <img src='/assets/images/icons/plant.png' alt='Rośliny' style={{ width: 24, height: 24, opacity: 0.5 }} />
            </IconButton>
          </span>
        </Tooltip>
        ),
    <Tooltip title='Zadania' key='tasks'>
      <IconButton
        style={{ color: '#47a145' }}
        onClick={() => navigate('/tasks')}
        sx={{}}
      >
        <AssignmentIcon />
      </IconButton>
    </Tooltip>,
    <Tooltip title='Projekty' key='projects'>
      <IconButton
        style={{ color: '#47a145' }}
        onClick={() => navigate('/projects')}
        sx={{}}
      >
        <img src='/assets/images/icons/project.png' alt='Projekty' style={{ width: 24, height: 24 }} />
      </IconButton>
    </Tooltip>,
    <Tooltip title='Wiadomości' key='messages'>
      <IconButton
        style={{ color: '#47a145' }}
        onClick={() => navigate('/messages')}
        sx={{}}
      >
        <MessageIcon />
      </IconButton>
    </Tooltip>,
    <Tooltip title='Profil użytkownika' key='profile'>
      <IconButton
        style={{ color: '#47a145' }}
        onClick={() => navigate('/user-profile')}
        sx={{}}
      >
        <PersonIcon />
      </IconButton>
    </Tooltip>
  ]

  // Łączymy ikony z propsów z domyślnymi ikonami
  const allIcons = [...rightIcons, ...defaultIcons]

  return (
    <nav style={{
      width: '100%',
      background: '#fff',
      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      justifyContent: 'flex-start',
      padding: 0,
      minHeight: '56px',
      zIndex: 1000
    }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 24px',
        minHeight: '56px'
      }}
      >
        {/* Logo po lewej */}
        <div style={{ display: 'flex', alignItems: 'center', minWidth: 120 }}>
          <img
            src='/assets/images/logo/logo-white.png'
            alt='logo'
            style={{ height: 36, width: 'auto', objectFit: 'contain', display: 'none' }}
          />
          <img
            src='/assets/images/logo/logo-black.png'
            alt='logo'
            style={{ height: 36, width: 'auto', objectFit: 'contain', cursor: 'pointer' }}
            onClick={() => navigate('/')}
          />
        </div>
        {/* Tekst na środku */}
        {!isMobile && !!centerText && (
          <div style={{ flex: 1, textAlign: 'center', fontWeight: 600, fontSize: 20, color: '#222' }}>
            {centerText}
          </div>
        )}
        {/* Ikony po prawej */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 120, justifyContent: 'flex-end', position: 'relative' }}>
          {!isMobile && allIcons.map((icon, idx) => (
            <span key={idx} style={{ display: 'inline-flex', alignItems: 'center' }}>{icon}</span>
          ))}
          {isMobile && (
            <>
              <button
                onClick={() => setDropdownOpen((open) => !open)}
                style={{
                  background: 'none',
                  border: 'none',
                  outline: 'none',
                  cursor: 'pointer',
                  padding: 8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 28,
                  color: '#1976d2'
                }}
                aria-label='Otwórz menu'
              >
                <svg width='28' height='28' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                  <rect y='4' width='24' height='2.5' rx='1.25' fill='currentColor' />
                  <rect y='10.75' width='24' height='2.5' rx='1.25' fill='currentColor' />
                  <rect y='17.5' width='24' height='2.5' rx='1.25' fill='currentColor' />
                </svg>
              </button>
              {dropdownOpen && (
                <div
                  id='mobile-icons-dropdown'
                  style={{
                    position: 'absolute',
                    top: '48px',
                    right: 0,
                    background: '#fff',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                    borderRadius: 8,
                    padding: 12,
                    zIndex: 2000,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 16
                  }}
                >
                  {allIcons.map((icon, idx) => (
                    <span key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>{icon}</span>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {/* Zakładki pod tekstem */}
      {(tabs != null) && tabs.length > 0 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          background: '#fafbfc',
          borderBottom: '1px solid #eee',
          padding: 0
        }}
        >
          {tabs.map(tab => (
            <button
              key={tab.value}
              onClick={() => (onTabChange != null) && onTabChange(tab.value)}
              style={{
                background: 'none',
                border: 'none',
                outline: 'none',
                fontWeight: 500,
                fontSize: 18,
                color: activeTab === tab.value ? '#1976d2' : '#555',
                padding: '20px 32px 12px 32px',
                cursor: 'pointer',
                borderBottom: activeTab === tab.value ? '3px solid #1976d2' : '3px solid transparent',
                transition: 'color 0.2s, border-bottom 0.2s',
                letterSpacing: 1,
                textTransform: 'uppercase',
                backgroundColor: 'transparent'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}
    </nav>
  )
}

export default AppNavBar
